import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const user = await login(email, password);
      if (user.force_password_change) {
        toast.info("Please set a new password to continue");
        navigate("/change-password");
      } else if (isAdmin || ["superadmin", "editor", "analyst", "social_manager"].includes(user.role)) {
        navigate(isAdmin ? "/admin" : "/");
      } else {
        navigate("/");
      }
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md bg-[#121212] border border-white/10 rounded-xl p-8" data-testid="login-form">
        <div className="flex justify-center mb-6"><Logo /></div>
        <h1 className="font-heading font-bold text-white text-xl text-center mb-1">
          {isAdmin ? "Admin Access" : "Welcome back"}
        </h1>
        <p className="text-gray-500 text-sm text-center mb-8">
          {isAdmin ? "Restricted area — authorized operators only" : "Sign in to your NewsLayer account"}
        </p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="email" className="text-gray-300">Email</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
              className="mt-1.5 bg-[#1A1A1A] border-white/10 text-white" />
          </div>
          <div>
            <Label htmlFor="password" className="text-gray-300">Password</Label>
            <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              className="mt-1.5 bg-[#1A1A1A] border-white/10 text-white" />
          </div>
          {error && <p className="text-red-400 text-sm" data-testid="login-error">{error}</p>}
          <Button type="submit" disabled={busy} data-testid="login-submit-button"
            className="w-full bg-[#00E5FF] text-black font-bold hover:bg-[#00c9e0]">
            {busy ? "Signing in..." : "Sign in"}
          </Button>
        </form>
        <p className="text-center mt-4">
          <Link to="/forgot-password" data-testid="login-forgot-link"
            className="font-mono text-xs text-gray-500 hover:text-[#00E5FF] transition-colors">
            Forgot password?
          </Link>
        </p>
        {!isAdmin && (
          <p className="text-gray-500 text-sm text-center mt-6">
            No account?{" "}
            <Link to="/signup" data-testid="login-signup-link" className="text-[#00E5FF] hover:underline">Create one free</Link>
          </p>
        )}
      </div>
    </div>
  );
}
